import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

interface Toast {
  id: string
  title: string
  description?: string
  type: 'success' | 'error' | 'warning' | 'info'
  duration?: number
}

interface UIState {
  theme: 'light' | 'dark' | 'system'
  sidebarOpen: boolean
  toasts: Toast[]
  isConnected: boolean
  connectionStatus: 'connected' | 'connecting' | 'disconnected' | 'error'
  
  // Actions
  setTheme: (theme: 'light' | 'dark' | 'system') => void
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
  clearToasts: () => void
  setConnectionStatus: (status: 'connected' | 'connecting' | 'disconnected' | 'error') => void
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      theme: 'system',
      sidebarOpen: true,
      toasts: [],
      isConnected: false,
      connectionStatus: 'disconnected',

      setTheme: (theme: 'light' | 'dark' | 'system') => {
        set({ theme })
        
        // Apply theme to document
        const root = document.documentElement
        root.classList.remove('light', 'dark')
        
        if (theme === 'system') {
          const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
          root.classList.add(systemTheme)
        } else {
          root.classList.add(theme)
        }
      },

      toggleSidebar: () => {
        set((state) => ({ sidebarOpen: !state.sidebarOpen }))
      },

      setSidebarOpen: (open: boolean) => {
        set({ sidebarOpen: open })
      },

      addToast: (toast: Omit<Toast, 'id'>) => {
        const id = Math.random().toString(36).substr(2, 9)
        const newToast: Toast = { id, ...toast }
        
        set((state) => ({
          toasts: [...state.toasts, newToast],
        }))

        // Auto-remove toast after duration
        const duration = toast.duration ?? 5000
        if (duration > 0) {
          setTimeout(() => {
            get().removeToast(id)
          }, duration)
        }
      },

      removeToast: (id: string) => {
        set((state) => ({
          toasts: state.toasts.filter((toast) => toast.id !== id),
        }))
      },

      clearToasts: () => {
        set({ toasts: [] })
      },

      setConnectionStatus: (status: 'connected' | 'connecting' | 'disconnected' | 'error') => {
        set({ 
          connectionStatus: status,
          isConnected: status === 'connected',
        })
      },
    }),
    {
      name: 'risklens-ui',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        theme: state.theme,
        sidebarOpen: state.sidebarOpen,
      }),
    }
  )
)